/**
 * V2 Survey Section types. `SurveyQuestionBase` extends V1's `IFormField<string>` (carrying label,
 * fieldType, options, etc. forward) and adds the section/discriminator layer (`_type`, `fieldId`,
 * `visibleWhen`). `_type` mirrors the BE `SurveyPart` JsonPolymorphic discriminator.
 */
import { IFormField } from '@ViewModels';
import type { NumericComparisonOperator } from '@ViewModels';
import { QuestionType } from '../FormFieldWithOptions/questionTypes';

// ---------------------------------------------------------------------------
// Question discriminator
// ---------------------------------------------------------------------------

/** The V2 question discriminator — single source of truth lives in `questionTypes`. */
export type QuestionFieldType = QuestionType;

// ---------------------------------------------------------------------------
// Conditional visibility
// ---------------------------------------------------------------------------

export interface QuestionVisibilitySimple {
	_type: 'SimpleEqualityQuestionVisibility';
	fieldId: string;
	fieldValue: string;
}

export interface QuestionVisibilityLogical {
	_type: 'LogicalQuestionVisibility';
	visibilities: QuestionVisibility[];
	logicalOperator: 'And' | 'Or';
}

export interface QuestionVisibilityComparison {
	_type: 'ComparisonQuestionVisibility';
	fieldId: string;
	op: NumericComparisonOperator;
	value: number;
}

export type QuestionVisibility = QuestionVisibilitySimple | QuestionVisibilityLogical | QuestionVisibilityComparison;

/**
 * Which question kinds drive which rule. One definition: were the authoring controls and the
 * cleanup sweep to disagree, the editor would author a rule the next sweep silently deletes.
 */
export const OPTION_SOURCE_TYPES: ReadonlySet<string> = new Set(['DropDownQuestion', 'CheckBoxesQuestion']);
export const NUMERIC_SOURCE_TYPES: ReadonlySet<string> = new Set(['LinearScaleQuestion', 'NpsQuestion']);

// ---------------------------------------------------------------------------
// Survey questions — extend V1 IFormField<string> to carry forward all
// existing question fields (label, fieldType, options, isOptional, etc.)
// ---------------------------------------------------------------------------

// Omit `_type` from IFormField (its ApiModelType union excludes V2 discriminators) and re-add ours.
interface SurveyQuestionBase extends Omit<IFormField<string>, '_type'> {
	id: string;
	fieldId: string;
	_type: QuestionFieldType;
	visibleWhen?: QuestionVisibility;
	// Scale config (LinearScale/NPS only) — lives on the surveyPart, round-trips via surveyForm.
	minValue?: number;
	maxValue?: number;
	startLabel?: string | null;
	endLabel?: string | null;
}

export interface ShortAnswerQuestion extends SurveyQuestionBase {
	_type: 'ShortAnswerQuestion';
}

export interface LongAnswerQuestion extends SurveyQuestionBase {
	_type: 'LongAnswerQuestion';
}

export interface DropDownQuestion extends SurveyQuestionBase {
	_type: 'DropDownQuestion';
}

export interface CheckBoxesQuestion extends SurveyQuestionBase {
	_type: 'CheckBoxesQuestion';
}

export interface FileUploadQuestion extends SurveyQuestionBase {
	_type: 'FileUploadQuestion';
}

export interface LinearScaleQuestion extends SurveyQuestionBase {
	_type: 'LinearScaleQuestion';
}

export interface NpsQuestion extends SurveyQuestionBase {
	_type: 'NpsQuestion';
}

export interface YesNoQuestion extends SurveyQuestionBase {
	_type: 'YesNoQuestion';
}

export type SurveyQuestion =
	| ShortAnswerQuestion
	| LongAnswerQuestion
	| DropDownQuestion
	| CheckBoxesQuestion
	| FileUploadQuestion
	| LinearScaleQuestion
	| NpsQuestion
	| YesNoQuestion;

// ---------------------------------------------------------------------------
// Survey section
// ---------------------------------------------------------------------------

export interface SurveySection {
	_type: 'SurveySection';
	id: string;
	title?: string;
	description?: string;
	questions: SurveyQuestion[];
	visibleWhen?: QuestionVisibility;
}

// ---------------------------------------------------------------------------
// Top-level union and type guards
// ---------------------------------------------------------------------------

export type SurveyPart = SurveySection | SurveyQuestion;

// Null-safe: a write to an index the array no longer has leaves a hole that would
// otherwise throw here while rendering the section list.
export const isSurveySection = (part: SurveyPart | undefined | null): part is SurveySection =>
	part?._type === 'SurveySection';
